import { Col, Radio, Row } from "antd";
import to from "await-to-js";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { omit } from "lodash";
import { Button } from "../../../../components/Button/Button";
import { ModalsContext } from "../../../../context/modals.context";
import {
  DUVAN_FIELDS_EXCISE_LICENCE,
  DUVAN_FIELDS_EXCISE_WAREHOUSE,
  OTHER_FIELDS_EXCISE_LICENCE,
  OTHER_FIELDS_EXCISE_WAREHOUSE,
} from "../../../../modules/obligee/exciseLicences/exciseLicences.constants";
import { ExciseLicencesContext } from "../../../../modules/obligee/exciseLicences/exciseLicences.context";
import { exciseLicencesService } from "../../../../modules/obligee/exciseLicences/exciseLicences.service";
import { ISkladisteInformacijeProps } from "../../../../modules/obligee/exciseLicences/exciseLicences.types";
import { AkciznaMarkicaKategorijaProzivoda } from "../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { exciseWarehouseService } from "../../../../modules/obligee/exciseWarehouse/exciseWarehouse.service";
import { InformationAbout } from "../../../Ministry/components/InformationAbout/InformationAbout";
import { BottomPart } from "../components/BottomPart/BottomPart";
import { CenterPart } from "../components/CenterPart/CenterPart";
import { TopPart } from "../components/TopPart/TopPart";
import { RenewCancelLicenceModal } from "./components/RenewCancelLicenceModal/RenewCancelLicenceModal";
import style from "./ExciseLicencesRequestForm.module.scss";

interface IExciseLicencesRequestFormProps {
  type: string;
}

export const ExciseLicencesRequestForm: React.FC<
  IExciseLicencesRequestFormProps
> = ({ type }) => {
  const { handleUpdateModals, setError } = useContext(ModalsContext);
  const [meraVelicinaSkladistaError, setMeraVelicineSkladistaError] = useState<
    string | null
  >(null);
  const {
    exciseLicences,
    setExciseLicences,
    setInformacijeDozvole,
    setInformacijeSkladista,
    clearPayloadState,
  } = useContext(ExciseLicencesContext);
  const [kategorijaProizvodaId, setKategorijaProizvodaId] =
    useState<AkciznaMarkicaKategorijaProzivoda | null>(null);

  const [licenceRequest, setLicenceRequest] = useState(null);

  let queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id");

  const navigate = useNavigate();

  const postVracanjeDodatnihInformacijaZaKreiranjeDozvole = async () => {
    const [err, res] = await to(
      exciseLicencesService.postVracanjeDodatnihInformacijaZaKreiranjeDozvole(
        Number(id) || null
      )
    );
    if (err || !res.success) return setError(res?.errorMessage);
    const selectedSkladiste = res.data.skladisteInformacije.find(
      (skladiste: ISkladisteInformacijeProps) =>
        skladiste.skladisteId === Number(id)
    );

    // TODO: Revisit this logic -> If no id is found as match app will break
    if (id) {
      setKategorijaProizvodaId(
        res.data.dozvolaInformacije?.kategorijaProizvodaId
      );
      setExciseLicences({
        skladisteInformacijePayload: selectedSkladiste,
        dozvolaInformacije: res.data.dozvolaInformacije,
        dozvolaInformacijeSkladista: res.data.dozvolaInformacije,
        userInformationDozvola: res.data.userInformationDozvola,
        skladisteInformacije: res.data.skladisteInformacije,
        mereVelicine: res.data.mereVelicine,
        dokumentiZaDozvoluSkladista: res.data.dokumentiZaDozvolu,
      });
    } else {
      setKategorijaProizvodaId(
        res.data.dozvolaInformacije?.kategorijaProizvodaId
      );
      setExciseLicences({
        dozvolaInformacijeSkladista: res.data.dozvolaInformacije,
        userInformationDozvola: res.data.userInformationDozvola,
        skladisteInformacije: res.data.skladisteInformacije,
        mereVelicine: res.data.mereVelicine,
      });
    }
  };

  const postKreiranjeZahtevaZaIzmenuObnovuIliPrekidVazenjaDozvole =
    async () => {
      setMeraVelicineSkladistaError(null);

      const dokumentiPayload = exciseLicences.dokumentiZaDozvolu.map(
        (dokument) => {
          return {
            // condition: dokument.condition,
            data: dokument.data,
            dozvolaDocumentType: dokument.dozvolaDocumentType,
            format: dokument.format,
          };
        }
      );

      if (
        !exciseLicences?.skladisteInformacijePayload?.meraVelicineSkladista ||
        !exciseLicences?.skladisteInformacijePayload?.meraVrednost
      ) {
        if (
          !!exciseLicences?.skladisteInformacijePayload
            ?.meraVelicineSkladista &&
          exciseLicences?.skladisteInformacijePayload?.meraVrednost === 0
        ) {
          setMeraVelicineSkladistaError(
            t("COMMON.VREDNOST_MORA_DA_BUDE_VECA_OD_NULE")
          );
        } else {
          setMeraVelicineSkladistaError(t("COMMON.MERA_JE_OBAVEZNA"));
        }

        return;
      }

      const dozvolaInformacijePayload =
        kategorijaProizvodaId === AkciznaMarkicaKategorijaProzivoda.DUVAN
          ? omit(exciseLicences.dozvolaInformacije, OTHER_FIELDS_EXCISE_LICENCE)
          : omit(
              exciseLicences.dozvolaInformacije,
              DUVAN_FIELDS_EXCISE_LICENCE
            );

      const skladisteInformacijePayload =
        kategorijaProizvodaId === AkciznaMarkicaKategorijaProzivoda.DUVAN
          ? omit(
              exciseLicences.skladisteInformacijePayload,
              OTHER_FIELDS_EXCISE_WAREHOUSE
            )
          : omit(
              exciseLicences.skladisteInformacijePayload,
              DUVAN_FIELDS_EXCISE_WAREHOUSE
            );

      const payload = {
        tipZahteva: licenceRequest,
        note: null,
        zahtev: {
          tipDozvole: exciseLicences.tipDozvole,
          dokumentiZaDozvolu: dokumentiPayload,
          dozvolaInformacije: dozvolaInformacijePayload,
          skladisteInformacije: {
            ...skladisteInformacijePayload,
            skladisteId: Number(id),
          },
        },
      };

      const [err, res] = await to(
        exciseWarehouseService.postKreiranjeZahtevaZaIzmenuObnovuIliPrekidVazenjaDozvole(
          payload
        )
      );
      if (err || !res.success) return setError(res?.errorMessage);

      setExciseLicences({ data: res.data });

      if (licenceRequest === 1) navigate("/obveznik/skladista/zahtevPodnet");

      if (licenceRequest === 3) {
        navigate("/obveznik/skladista/zahtevUspesan", {
          state: {
            title: t("SKLADISTA.USPESNO_STE_PODNELI_ZAHTEV_ZA_OBNOVU_DOZVOLE"),
            redirectPath: "/obveznik/skladista/listaSkladista",
          },
        });
      }

      if (licenceRequest === 2) {
        navigate("/obveznik/skladista/zahtevUspesan", {
          state: {
            title: t("SKLADISTA.USPESNO_STE_IZMENILI_ZAHTEV_ZA_DOZVOLU"),
            redirectPath: "/obveznik/skladista/listaSkladista",
          },
        });
      }

      if (licenceRequest === 4) {
        navigate("/obveznik/skladista/zahtevUspesan", {
          state: {
            title: t("SKLADISTA.USPESNO_STE_PODNELI_ZAHTEV_ZA_PREKID"),
            redirectPath: "/obveznik/skladista/listaSkladista",
          },
        });
      }

      clearPayloadState();
    };

  const postKreiranjeZahtevaDozvole = async () => {
    setMeraVelicineSkladistaError(null);

    const dokumentiPayload = exciseLicences.dokumentiZaDozvolu.map(
      (dokument) => {
        return {
          // condition: dokument.condition,
          data: dokument.data,
          dozvolaDocumentType: dokument.dozvolaDocumentType,
          format: dokument.format,
        };
      }
    );

    if (
      !exciseLicences?.skladisteInformacijePayload?.meraVelicineSkladista ||
      !exciseLicences?.skladisteInformacijePayload?.meraVrednost
    ) {
      if (
        !!exciseLicences?.skladisteInformacijePayload?.meraVelicineSkladista &&
        exciseLicences?.skladisteInformacijePayload?.meraVrednost === 0
      ) {
        setMeraVelicineSkladistaError(
          t("COMMON.VREDNOST_MORA_DA_BUDE_VECA_OD_NULE")
        );
      } else {
        setMeraVelicineSkladistaError(t("COMMON.MERA_JE_OBAVEZNA"));
      }

      return;
    }

    const dozvolaInformacijePayload =
      kategorijaProizvodaId === AkciznaMarkicaKategorijaProzivoda.DUVAN
        ? omit(exciseLicences.dozvolaInformacije, OTHER_FIELDS_EXCISE_LICENCE)
        : omit(exciseLicences.dozvolaInformacije, DUVAN_FIELDS_EXCISE_LICENCE);

    const skladisteInformacijePayload =
      kategorijaProizvodaId === AkciznaMarkicaKategorijaProzivoda.DUVAN
        ? omit(
            exciseLicences.skladisteInformacijePayload,
            OTHER_FIELDS_EXCISE_WAREHOUSE
          )
        : omit(
            exciseLicences.skladisteInformacijePayload,
            DUVAN_FIELDS_EXCISE_WAREHOUSE
          );

    const payload = {
      tipDozvole: exciseLicences.tipDozvole,
      dokumentiZaDozvolu: dokumentiPayload,
      dozvolaInformacije: dozvolaInformacijePayload,
      skladisteInformacije: {
        ...skladisteInformacijePayload,
        skladisteId: exciseLicences.selectedSkladisteId,
      },
    };
    const [err, res] = await to(
      exciseLicencesService.postKreiranjeZahtevaDozvole(payload)
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setExciseLicences({ data: res.data });
    navigate("/obveznik/akcizneDozvole/zahtevPodnet");
    clearPayloadState();
  };

  const handleSendRequest = () => {
    if (type === "dozvole") {
      postKreiranjeZahtevaDozvole();
    } else {
      if (licenceRequest === 2)
        postKreiranjeZahtevaZaIzmenuObnovuIliPrekidVazenjaDozvole();
      if (licenceRequest === 3)
        postKreiranjeZahtevaZaIzmenuObnovuIliPrekidVazenjaDozvole();
      if (licenceRequest === 4)
        handleUpdateModals({ modalVisible: "renewCancel" });
    }
  };

  const handleRadio = (event: any) => {
    setLicenceRequest(event.target.value);
  };

  const handleTypeHeader = () => {
    if (type === "dozvole")
      return <h2>{t("AKCIZNE_DOZVOLE.NOVI_ZAHTEV_ZA_DOZVOLU")}</h2>;
    return (
      <div>
        <h2>{t("AKCIZNE_DOZVOLE.OVU_DOZVOLU")}</h2>
        <Radio.Group onChange={handleRadio}>
          <Radio value={2}>{t("AKCIZNE_DOZVOLE.IZMENI")}</Radio>
          <Radio value={3}>{t("AKCIZNE_DOZVOLE.OBNOVI")}</Radio>
          <Radio value={4}>{t("AKCIZNE_DOZVOLE.PREKINI_VAZENJE")}</Radio>
        </Radio.Group>
      </div>
    );
  };

  const showInformationAbout = () => {
    if (type === "dozvole")
      return (
        <InformationAbout
          type={t("AKCIZNE_DOZVOLE.PODNOSILAC_ZAHTEVA")}
          dataLicence={exciseLicences.userInformationDozvola}
        />
      );
  };

  const getPreSelectedOption = () => {
    if (id) {
      const entitet = exciseLicences.skladisteInformacije.find(
        (skladiste: ISkladisteInformacijeProps) =>
          skladiste.skladisteId === Number(id)
      );

      setExciseLicences({
        tipDozvole: exciseLicences?.tipDozvole,
        dozvolaInformacije: {
          kategorijaProizvodaId:
            exciseLicences.dozvolaInformacijeSkladista?.kategorijaProizvodaId,
          ulicaProizvodnje:
            exciseLicences.dozvolaInformacijeSkladista?.ulicaProizvodnje,
          brojUliceProizvodnje:
            exciseLicences.dozvolaInformacijeSkladista?.brojUliceProizvodnje,
          postanskiBrojProizvodnje:
            exciseLicences.dozvolaInformacijeSkladista
              ?.postanskiBrojProizvodnje,
          mestoProizvodnje:
            exciseLicences.dozvolaInformacijeSkladista?.mestoProizvodnje,
          kolicinaUskladistenihProizvoda:
            exciseLicences.dozvolaInformacijeSkladista
              ?.kolicinaUskladistenihProizvoda,
          prosecanMesecniRashod:
            exciseLicences.dozvolaInformacijeSkladista?.prosecanMesecniRashod,
          nacinOtpremanja:
            exciseLicences.dozvolaInformacijeSkladista?.nacinOtpremanja,
          podatakOMernimUredjajima:
            exciseLicences.dozvolaInformacijeSkladista
              ?.podatakOMernimUredjajima,
          brojResenja: exciseLicences.dozvolaInformacijeSkladista?.brojResenja,
          datumResenjaNadleznogOrgana:
            exciseLicences.dozvolaInformacijeSkladista
              ?.datumResenjaNadleznogOrgana,
          tarifnaOznaka:
            exciseLicences.dozvolaInformacijeSkladista?.tarifnaOznaka,
          kolicinaDuvanaKg:
            exciseLicences.dozvolaInformacijeSkladista?.kolicinaDuvanaKg,
          prosecnaKolicinaDuvanaKg:
            exciseLicences.dozvolaInformacijeSkladista
              ?.prosecnaKolicinaDuvanaKg,
          kolicinaPojedinacnihPakovanjaKomadi:
            exciseLicences.dozvolaInformacijeSkladista
              ?.kolicinaPojedinacnihPakovanjaKomadi,
          prosecnaKolicinaPojedinacnihPakovanjaKomadi:
            exciseLicences.dozvolaInformacijeSkladista
              ?.prosecnaKolicinaPojedinacnihPakovanjaKomadi,
        },
        skladisteInformacijePayload: {
          skladisteId: entitet?.skladisteId,
          planiranaProizvodnjaProizvodaKomadi:
            entitet?.planiranaProizvodnjaProizvodaKomadi,
          planiranaProizvodnjaProizvodaKg:
            entitet?.planiranaProizvodnjaProizvodaKg,
          kolicinaProizvodaIzPrethodneGodineKomadi:
            entitet?.kolicinaProizvodaIzPrethodneGodineKomadi,
          kolicinaProizvodaIzPrethodneGodine:
            entitet?.kolicinaProizvodaIzPrethodneGodine,
          kolicinaProizvodaIzPrethodneGodineKg:
            entitet?.kolicinaProizvodaIzPrethodneGodineKg,
          planiranaProizvodnjaProizvoda: entitet?.planiranaProizvodnjaProizvoda,
          brojSkladistaZaDozvolu: entitet?.brojSkladistaZaDozvolu,
          ulicaSkladista: entitet?.ulicaSkladista,
          brojUliceSkladista: entitet?.brojUliceSkladista,
          postanskiBrojSkladista: entitet?.postanskiBrojSkladista,
          mestoSkladista: entitet?.mestoSkladista,
          oznakaSkladista: entitet?.oznakaSkladista,
          meraVelicineSkladista: entitet?.meraVelicineSkladista,
          meraVrednost: entitet?.meraVrednost,
          opisSkladista: entitet?.opisSkladista,
          opisRadaRacunovodstva: entitet?.opisRadaRacunovodstva,
        },
      });

      setKategorijaProizvodaId(
        exciseLicences.dozvolaInformacijeSkladista?.kategorijaProizvodaId
      );
    }
    return null;
  };

  const isDisabled = () => {
    if (licenceRequest === 2 || licenceRequest === 3 || licenceRequest === 4)
      return false;
    if (id) return true;
  };

  useEffect(() => {
    getPreSelectedOption();
  }, [licenceRequest]);

  useEffect(() => {
    postVracanjeDodatnihInformacijaZaKreiranjeDozvole();

    return () => {
      clearPayloadState();
    };
  }, []);

  return (
    <Row className={style.mainContainer}>
      <RenewCancelLicenceModal />
      <Col span={24}>{handleTypeHeader()}</Col>
      <div className={style.contentWrapper}>
        {showInformationAbout()}
        <TopPart
          filledData={exciseLicences.dozvolaInformacijeSkladista}
          licenceRequest={licenceRequest}
          stateValues={exciseLicences.dozvolaInformacije as any}
          data={exciseLicences.userInformationDozvola}
          setInformacijeDozvole={setInformacijeDozvole}
          tipDozvole={exciseLicences.tipDozvole}
          kategorijaProizvodaId={kategorijaProizvodaId}
          setKategorijaProizvodaId={(id: AkciznaMarkicaKategorijaProzivoda) => {
            setKategorijaProizvodaId(id);
          }}
        />
        <CenterPart
          stateValues={exciseLicences.skladisteInformacijePayload}
          data={exciseLicences.skladisteInformacije}
          licenceRequest={licenceRequest}
          meraSelect={exciseLicences.mereVelicine}
          selectedSkladisteId={exciseLicences.selectedSkladisteId}
          setInformacijeSkladista={setInformacijeSkladista}
          setExciseLicences={setExciseLicences}
          meraVelicinaSkladistaError={meraVelicinaSkladistaError}
          kategorijaProizvodaId={kategorijaProizvodaId}
        />
        <BottomPart
          data={exciseLicences.dokumentiZaDozvoluSkladista}
          isAddDocumentDisabled={isDisabled()}
          isRequired={type === "dozvole"}
        />
      </div>
      <Col className={style.buttonCol} span={24}>
        <Button
          type="primary"
          onClick={handleSendRequest}
          disabled={isDisabled()}
        >
          {t("AKCIZNE_DOZVOLE.POSALJI_ZAHTEV")}
        </Button>
      </Col>
    </Row>
  );
};
